// Payments ledger — projection of orders by payment state, with running totals.
// Until Stripe is wired (Phase 5), payment_ref is empty for everything.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { StatusBadge } from './Overview'

interface Row {
  id: string; number: number; status: string; totalCents: number; currency: string
  paymentRef: string | null
  createdAt: string; paidAt: string | null
  customerEmail: string | null; shippingName: string | null
}
interface Resp {
  items: Row[]
  totals: { paidCents: number; outstandingCents: number; refundedCents: number }
}

export default function Payments() {
  const { getAccessToken } = useAuth()
  const [state, setState] = useState<'all'|'pending'|'paid'|'refunded'>('all')
  const [data, setData] = useState<Resp | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<Resp>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/payments?state=${state}`)
        setData(d); setErr(null)
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="eyebrow">Processing</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Payments</h1>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card"><div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Paid</div><div className="font-heading font-bold text-2xl text-ink mt-1">${((data?.totals.paidCents ?? 0) / 100).toFixed(2)}</div></div>
        <div className="card"><div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Outstanding</div><div className="font-heading font-bold text-2xl text-ink mt-1">${((data?.totals.outstandingCents ?? 0) / 100).toFixed(2)}</div></div>
        <div className="card"><div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Refunded</div><div className="font-heading font-bold text-2xl text-ink mt-1">${((data?.totals.refundedCents ?? 0) / 100).toFixed(2)}</div></div>
      </div>

      <div className="flex gap-1.5 mb-4 border-b border-border pb-1">
        {(['all','pending','paid','refunded'] as const).map((s) => (
          <button key={s} onClick={() => setState(s)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
              (state === s ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>{s}</button>
        ))}
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Order</th>
              <th className="text-left px-4 py-2 font-bold">Customer</th>
              <th className="text-left px-4 py-2 font-bold">Status</th>
              <th className="text-left px-4 py-2 font-bold">Payment ref</th>
              <th className="text-right px-4 py-2 font-bold">Amount</th>
              <th className="text-right px-4 py-2 font-bold">Paid at</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data?.items ?? []).length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No payments in this state.</td></tr>}
            {(data?.items ?? []).map((r) => (
              <tr key={r.id} className="hover:bg-surface transition-colors">
                <td className="px-4 py-3"><Link to={`/admin/orders/${r.id}`} className="font-mono text-xs text-ink-2 hover:text-gold-dark">#{r.number}</Link></td>
                <td className="px-4 py-3"><div className="text-ink truncate max-w-[220px]">{r.shippingName ?? '—'}</div><div className="text-xs text-ink-3 truncate max-w-[220px]">{r.customerEmail ?? '—'}</div></td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-ink-3 font-mono text-xs">{r.paymentRef ?? '—'}</td>
                <td className="px-4 py-3 text-right font-semibold text-ink">${(r.totalCents / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
